import type { UseRadioProps } from '@chakra-ui/react';
import { Box, Text, useRadio } from '@chakra-ui/react';
import type { SanityImage } from '@time-line/components/types';
import { useImage } from 'lib/sanity';
import Image from 'next/image';
import { type ReactNode, useId } from 'react';

interface RadioCardProps extends UseRadioProps {
  image: SanityImage;
  children: ReactNode;
}

export function RadioCard({ image, children, ...props }: RadioCardProps) {
  const imageProps = useImage(image, { fill: true });
  const labelId = useId();
  const { getInputProps, getRadioProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getRadioProps();

  return (
    <Box as="label">
      <input {...input} aria-labelledby={labelId} />
      <Box
        {...checkbox}
        overflow="hidden"
        pos="relative"
        cursor="pointer"
        bg="gray.50"
        borderWidth="1px"
        borderColor="blackAlpha.250"
        borderRadius="base"
        _checked={{
          borderColor: 'gray.600',
          '> span': {
            borderColor: 'gray.600',
          },
          '> span:before': {
            opacity: 1,
          },
        }}
        p="2xs"
        pt="80px"
      >
        <Box
          as="span"
          role="presentation"
          h="20px"
          w="20px"
          borderWidth="1px"
          borderColor="blackAlpha.500"
          borderRadius="full"
          pos="absolute"
          top="2xs"
          left="2xs"
          zIndex={1}
          sx={{
            '&::before': {
              opacity: 0,
              content: '""',
              pos: 'absolute',
              w: '8px',
              h: '8px',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bg: 'gray.600',
              borderRadius: 'full',
            },
          }}
        />
        <Text as="span" pos="relative" zIndex={1} size="title2" id={labelId}>
          {children}
        </Text>
        {imageProps && (
          <Box pos="absolute" inset={0}>
            <Image
              {...imageProps}
              alt=""
              sizes="(min-width: 960px) 15vw, 50vw"
              style={{ objectFit: 'cover' }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
